<template>
  <div
    class="dropdown-overlay"
    @click.self="closeDropdown"
    ref="refRoot"
    tabindex="0"
  >
    <div ref="dropdownRef" class="dropdown-content">
      <slot></slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';

import { useKeyPress } from '@/composables/shared/useKeyPress';

const props = defineProps({
  triggerRef: {
    type: Object,
    required: true,
  },
  position: {
    type: String,
    default: 'below',
    validator: (value) => ['right', 'below'].includes(value),
  },
});

const emit = defineEmits(['close']);
const refRoot = ref<HTMLElement | null>(null);
const dropdownRef = ref<HTMLDivElement | null>(null);

const closeDropdown = () => {
  emit('close');
};

const updateDropdownPosition = () => {
  if (props.triggerRef && dropdownRef.value) {
    const triggerRect = props.triggerRef.getBoundingClientRect();
    const dropdown = dropdownRef.value;
    const spaceAfterTrigger = 4;
    const buffer = 8;

    const modal = props.triggerRef.closest('.modal-content');

    if (props.position === 'below') {
      if (modal) {
        const modalRect = modal.getBoundingClientRect();
        dropdown.style.left = `${triggerRect.left - modalRect.left}px`;

        const availableSpaceInModal =
          modalRect.bottom - triggerRect.bottom - spaceAfterTrigger - buffer;

        dropdown.style.maxHeight = `${availableSpaceInModal}px`;
        dropdown.style.overflowY = 'auto';
        dropdown.style.top = `${triggerRect.bottom - modalRect.top + spaceAfterTrigger}px`;
      } else {
        dropdown.style.left = `${triggerRect.left}px`;
        dropdown.style.top = `${triggerRect.bottom + spaceAfterTrigger}px`;

        const availableSpaceBelow =
          window.innerHeight - triggerRect.bottom - spaceAfterTrigger - buffer;
        dropdown.style.maxHeight = `${availableSpaceBelow}px`;
        dropdown.style.overflowY = 'auto';
      }
    } else if (props.position === 'right') {
      if (modal) {
        const modalRect = modal.getBoundingClientRect();
        dropdown.style.top = `${triggerRect.top - modalRect.top}px`;
        dropdown.style.left = `${triggerRect.right - modalRect.left + spaceAfterTrigger}px`;
      } else {
        dropdown.style.top = `${triggerRect.top}px`;
        dropdown.style.left = `${triggerRect.right + spaceAfterTrigger}px`;
      }
    }
  }
};

useKeyPress(refRoot, {
  Escape: () => {
    emit('close');
  },
});

onMounted(() => {
  refRoot.value?.focus();
  document.body.style.overflow = 'hidden';
  updateDropdownPosition();
  window.addEventListener('resize', updateDropdownPosition);
  window.addEventListener('scroll', updateDropdownPosition);
});

onUnmounted(() => {
  document.body.style.overflow = '';
  window.removeEventListener('resize', updateDropdownPosition);
  window.removeEventListener('scroll', updateDropdownPosition);
});
</script>

<style scoped lang="scss">
.dropdown-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: $z-dropdown;
}

.dropdown-content {
  position: absolute;
  width: fit-content;
  height: fit-content;
  padding: 0.5rem;
  border: 1px solid var(--border-default);
  border-radius: 8px;
  background: var(--surface-default);
  box-shadow: $shadow-dropdown;
  overflow-y: auto;
}
</style>
